// ;
import PlanOptimizationView from '../components/PlanOptimizationView';
import OptimizationRouteView from '../components/OptimizationRouteView';
import { useTranslation } from 'react-i18next';
import { 
  Box, Button, IconButton, Stack, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions,
  Typography, Chip, Paper, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Divider, Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import CodeIcon from '@mui/icons-material/Code';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RouteIcon from '@mui/icons-material/Route';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../components/DataTable';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import PlanStatsCard from '../components/PlanStatsCard';
import { 
  listPlans, createPlan, updatePlan, deletePlan, getPlan, updatePlanProgramStore, updatePlanWorker,
  Plan, PlanCreate, PlanDetail,
  PlanProgramStoreUpdate, PlanWorkerUpdate, PlanWorkerCreate,
  getPlanTimeMatrixMeta, getSavedTimeMatrices, exportPlanToYandexExcel, exportPlanToYandexJson
} from '../services/planService';
import { listWorkers, Worker } from '../services/workerService';
// ;
// ;
import { listRegions, Region } from '../services/regionService';
import { listSettlements, Settlement } from '../services/settlementService';
// @ts-ignore // Suppress linter error for PlanFormDialog import, file exists
import PlanFormDialog from '../components/PlanFormDialog';
import PlanOptimizationCell from '../components/PlanOptimizationCell';
import GoogleORToolsCalendarPlanningCell from '../components/GoogleORToolsCalendarPlanningCell';
import TimeMatrixRequestDialog from '../components/TimeMatrixRequestDialog';
import TimeMatrixViewer from '../components/TimeMatrixViewer';
import Snackbar from '@mui/material/Snackbar';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PlanClustersView from '../components/PlanClustersView';
import { OptimizationResultsView } from '../components/OptimizationResultsView';
import TaskQueueStatus from '../components/TaskQueueStatus';
import {useCallback, useMemo, useState} from "react";
import {listPrograms} from "@/services";

// Validation schemas
// const createSchema = yup.object({
//   name: yup.string().required('Name is required'),
//   month: yup.string().required('Month is required').matches(/^\d{4}-\d{2}$/, 'Month must be in YYYY-MM format'),
//   region_id: yup.number().optional(),
//   settlement_id: yup.number().optional(),
//   program_ids: yup.array().of(yup.number().required()).required().min(1, 'At least one program is required'),
//   worker_ids: yup.array().of(yup.number().required()).required().min(1, 'At least one worker is required'),
// }).required();

// const updateSchema = yup.object({
//   name: yup.string().required('Name is required'),
//   month: yup.string().required('Month is required').matches(/^\d{4}-\d{2}$/, 'Month must be in YYYY-MM format'),
//   region_id: yup.number().optional(),
//   settlement_id: yup.number().optional(),
// }).required();

// Simple form types without strict validation
// type CreateFormValues = {
//   name: string;
//   month: string;
//   region_id?: number;
//   settlement_id?: number;
//   program_ids: number[];
//   worker_ids: number[];
// };
//
// type UpdateFormValues = {
//   name: string;
//   month: string;
//   region_id?: number;
//   settlement_id?: number;
// };

// Unified form type for both create and edit modes
type PlanFormValues = {
  name: string;
  month?: string; // Optional since backend doesn't require it
  start_date: string;
  days: number;
  region_id?: number;
  settlement_id?: number;
  program_ids?: number[];
  worker_ids: number[];
  plan_workers?: PlanWorkerCreate[]; // Added for edit mode
};

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/plans');
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openPlanDetail, setOpenPlanDetail] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [planDetailData, setPlanDetailData] = useState<PlanDetail | null>(null);
  // Simplified state - most state moved to PlanFormDialog component
  const [selectedRegionId] = useState<number | ''>('');
  const [selectedSettlementId] = useState<number | ''>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [userTimeMatrices, setUserTimeMatrices] = useState<{ [key: string]: number[][] }>({});
  const [timeMatrices, setTimeMatrices] = useState<{ [key: string]: number[][] }>({});
  const [matrixLogs, setMatrixLogs] = useState<any>(null);
  const [openTimeMatrixRequest, setOpenTimeMatrixRequest] = useState(false);
  const [openTimeMatrixViewer, setOpenTimeMatrixViewer] = useState(false);
  const [currentProvider, setCurrentProvider] = useState<string>('doublegis');
  const [matrixMeta, setMatrixMeta] = useState<any>(null);
  const [openClustersView, setOpenClustersView] = useState(false);
  const [clustersPlanData, setClustersPlanData] = useState<{ id: number; name: string } | null>(null);
  const [openOptimizationResults, setOpenOptimizationResults] = useState(false);
  const [optimizationPlanData, setOptimizationPlanData] = useState<{ id: number; name: string } | null>(null);

  // Function to load saved matrices when user explicitly requests them
  const loadMatricesFromMetadata = useCallback(async () => {
    if (!selectedPlan?.id || !matrixMeta?.providers) return;
    
    const allMatrices: { [key: string]: number[][] } = {};
    const allUserMatrices: { [key: string]: number[][] } = {};
    
    for (const [provider, _] of Object.entries(matrixMeta.providers)) {
      try {
        // Use the new endpoint to get only saved matrices (no generation)
        const matrixData = await getSavedTimeMatrices(selectedPlan.id, provider);
        if (matrixData.time_matrices) {
          Object.assign(allMatrices, matrixData.time_matrices);
        }
        if (matrixData.user_time_matrices) {
          Object.assign(allUserMatrices, matrixData.user_time_matrices);
        }
      } catch (_error) {
        console.error(`Error loading saved matrices for provider ${provider}:`, _error);
      }
    }
    
    setTimeMatrices(allMatrices);
    setUserTimeMatrices(allUserMatrices);
    setCurrentProvider(Object.keys(matrixMeta.providers)[0] || 'doublegis');
  }, [selectedPlan?.id, matrixMeta]);

  // Debounce helper
  // const debounce = (func: (...args: any[]) => void, wait: number) => {
  //   let timeout: NodeJS.Timeout;
  //   return (...args: any[]) => {
  //     clearTimeout(timeout);
  //     timeout = setTimeout(() => func(...args), wait);
  //   };
  // };

  // Debounced save handler
  // const _debouncedSaveMatrix = React.useRef(
  //   debounce(async (planId: number, provider: string, routeType: string, matrix: number[][]) => {
  //     await savePlanTimeMatrix(planId, provider, routeType, matrix);
  //     setSnackbarMsg('Матрица времени успешно сохранена');
  //     setSnackbarOpen(true);
  //   }, 500)
  // ).current;

  // Fetch data
  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: listRegions
  });

  const { data: settlements = [] } = useQuery<Settlement[]>({
    queryKey: ['settlements', selectedRegionId],
    queryFn: () => listSettlements(selectedRegionId || undefined),
    enabled: !!selectedRegionId
  });

  const { data: programs = [] } = useQuery({ queryKey: ['programs'], queryFn: listPrograms });
  const { data: plans = [], isLoading } = useQuery({ queryKey: ['plans'], queryFn: () => listPlans() });

  // Settlement queries moved to PlanFormDialog component

  // Update workers query to filter by region/settlement
  const { data: workers = [] } = useQuery<Worker[]>({
    queryKey: ['workers', true, selectedRegionId, selectedSettlementId],
    queryFn: () => {
      // Filter workers by region/settlement on frontend since backend might not support this yet
      return listWorkers({ available_only: true }).then(allWorkers => 
        allWorkers.filter(worker => {
          const matchesRegion = !selectedRegionId || worker.settlement?.region?.id === selectedRegionId;
          const matchesSettlement = !selectedSettlementId || worker.settlement?.id === selectedSettlementId;
          return matchesRegion && matchesSettlement;
        })
      );
    }
  });

  // Filter workers based on selected criteria
  // const _filteredWorkers = useMemo(() => {
  //   return workers.filter(worker => {
  //     if (workerFilter.transport && worker.transport_type !== workerFilter.transport) {
  //       return false;
  //     }
  //     if (workerFilter.settlement && worker.settlement?.name !== workerFilter.settlement) {
  //       return false;
  //     }
  //     if (workerFilter.searchText) {
  //       const searchLower = workerFilter.searchText.toLowerCase();
  //       return (
  //         worker.name.toLowerCase().includes(searchLower) ||
  //         worker.email.toLowerCase().includes(searchLower) ||
  //         worker.settlement?.name?.toLowerCase().includes(searchLower)
  //       );
  //     }
  //     return true;
  //   });
  // }, [workers, workerFilter]);
  //
  // // Get unique values for filters
  // const _transportTypes = useMemo(() =>
  //   Array.from(new Set(workers.map(w => w.transport_type))), [workers]);
  // const _workerSettlements = useMemo(() =>
  //   Array.from(new Set(workers.map(w => w.settlement?.name).filter(Boolean))), [workers]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setOpenCreateForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updatePlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setOpenEditForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePlan,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plans'] }),
  });

  const updateProgramStoreMutation = useMutation({
    mutationFn: ({ planId, ppsId, data }: { planId: number; ppsId: number; data: PlanProgramStoreUpdate }) => 
      updatePlanProgramStore(planId, ppsId, data),
    onSuccess: () => {
      if (selectedPlan) {
        fetchPlanDetail(selectedPlan.id!);
      }
    },
  });

  const updateWorkerMutation = useMutation({
    mutationFn: ({ planId, pwId, data }: { planId: number; pwId: number; data: PlanWorkerUpdate }) => 
      updatePlanWorker(planId, pwId, data),
    onSuccess: () => {
      if (selectedPlan) {
        fetchPlanDetail(selectedPlan.id!);
      }
    },
  });

  const updatePlanWorkersMutation = useMutation({
    mutationFn: ({ id, plan_workers }: { id: number; plan_workers: PlanWorkerCreate[] }) => 
      updatePlan(id, { plan_workers }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      if (selectedPlan) {
        fetchPlanDetail(selectedPlan.id!);
      }
    },
  });

  // Functions
  const fetchPlanDetail = async (planId: number) => {
    try {
      const detail = await getPlan(planId);
      setPlanDetailData(detail);
      
      // Only fetch matrix metadata, don't load matrices automatically
      try {
        const metaData = await getPlanTimeMatrixMeta(planId);
        setMatrixMeta(metaData);
      } catch (_error) {
        console.error('Error fetching matrix metadata:', _error);
        setMatrixMeta(null);
      }
    } catch (_error) {
      console.error('Failed to fetch plan detail:', _error);
    }
  };

  const handleEdit = async (plan: Plan) => {
    try {
      // Fetch complete plan details including workers before opening edit dialog
      const planDetails = await getPlan(plan.id!);
      setSelectedPlan(planDetails);
      setOpenEditForm(true);
    } catch (_error) {
      console.error('Failed to fetch plan details for editing:', _error);
      // Fallback to basic plan data if detailed fetch fails
      setSelectedPlan(plan);
      setOpenEditForm(true);
    }
  };

  const handleViewDetail = async (plan: Plan) => {
    setSelectedPlan(plan);
    await fetchPlanDetail(plan.id!);
    setOpenPlanDetail(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const handleExportExcel = async (plan: Plan) => {
    try {
      const blob = await exportPlanToYandexExcel(plan.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan_${plan.id}_yandex_calendar_${plan.name.replace(' ', '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMsg('Excel file exported successfully');
      setSnackbarOpen(true);
    } catch (_error) {
      console.error('Export failed:', _error);
      setSnackbarMsg('Failed to export Excel file');
      setSnackbarOpen(true);
    }
  };

  const handleExportJson = async (plan: Plan) => {
    try {
      const jsonData = await exportPlanToYandexJson(plan.id);
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan_${plan.id}_yandex_calendar_${plan.name.replace(' ', '_')}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMsg('JSON file exported successfully');
      setSnackbarOpen(true);
    } catch (_error) {
      console.error('Export failed:', _error);
      setSnackbarMsg('Failed to export JSON file');
      setSnackbarOpen(true);
    }
  };

  const handleViewClusters = (plan: Plan) => {
    setClustersPlanData({ id: plan.id!, name: plan.name });
    setOpenClustersView(true);
  };

  const handleViewOptimizationResults = (plan: Plan) => {
    setOptimizationPlanData({ id: plan.id!, name: plan.name });
    setOpenOptimizationResults(true);
  };

  const handleCreateSubmit = (values: PlanFormValues) => {
    // Basic validation
    if (!values.name || !values.start_date || !values.days || !values.program_ids?.length || !values.worker_ids.length) {
      return;
    }
    // Create the payload with required program_ids and plan_workers
    const createPayload: PlanCreate = {
      name: values.name,
      start_date: values.start_date,
      days: values.days,
      region_id: values.region_id,
      settlement_id: values.settlement_id,
      program_ids: values.program_ids!, // We know this exists due to validation above
      plan_workers: values.plan_workers || [] // Use complete worker data
    };
    createMutation.mutate(createPayload);
  };

  const handleUpdateSubmit = (values: PlanFormValues) => {
    if (selectedPlan && values.name && values.start_date && values.days) {
      // For edit mode, also update worker assignments if they've changed
      if (values.plan_workers) {
        updatePlanWorkersMutation.mutate({ 
          id: selectedPlan.id!, 
          plan_workers: values.plan_workers 
        });
      }
      // Update basic plan info
      updateMutation.mutate({ 
        id: selectedPlan.id!, 
        data: {
          name: values.name,
          start_date: values.start_date,
          days: values.days,
          region_id: values.region_id,
          settlement_id: values.settlement_id
        }
      });
    }
  };

  const handleProgramStoreUpdate = (ppsId: number, field: string, value: any) => {
    if (!selectedPlan || !planDetailData) return;
    
    const update: PlanProgramStoreUpdate = {
      [field]: value,
      [`${field}_modified`]: true,
    };
    
    updateProgramStoreMutation.mutate({
      planId: selectedPlan.id!,
      ppsId,
      data: update,
    });
  };

  const handleWorkerUpdate = (pwId: number, field: string, value: any) => {
    if (!selectedPlan || !planDetailData) return;
    
    const update: PlanWorkerUpdate = {
      [field]: value,
      [`${field}_modified`]: true,
    };
    
    updateWorkerMutation.mutate({
      planId: selectedPlan.id!,
      pwId,
      data: update,
    });
  };

  // Define table columns for the plans list
  const columns = useMemo(() => [
  { field: 'name', headerName: t('plans.planName'), width: 200, sortable: true },
  { 
    field: 'start_date', 
    headerName: t('plans.startDate'), 
    width: 120, 
    sortable: true,
    valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : '-'
  },
  { 
    field: 'days', 
    headerName: t('plans.days'), 
    width: 80, 
    sortable: true,
    valueFormatter: (value: number) => value || '-'
  },
  { 
    field: 'program_count', 
    headerName: t('plans.programs'), 
    width: 120,
    sortable: false,
    renderCell: (params: any) => (
      <Chip label={params.row.plan_program_stores?.length || 0} size="small" color="primary" />
    )
  },
  { 
    field: 'worker_count', 
    headerName: t('navigation.workers'), 
    width: 120,
    sortable: false,
    renderCell: (params: any) => (
      <Chip label={params.row.plan_workers?.length || 0} size="small" color="secondary" />
    )
  },
  { 
    field: 'stats', 
    headerName: t('plans.statistics'), 
    width: 300,
    sortable: false,
    renderCell: (params: any) => (
      <PlanStatsCard stats={params.row.stats} compact={true} />
    )
  },
  { 
    field: 'created_at', 
    headerName: t('plans.created'), 
    width: 150, 
    sortable: true, 
    valueFormatter: (value: string) => new Date(value).toLocaleDateString() 
  },
  {
    field: 'calendar_planning',
    headerName: t('plans.yandexCalendar'),
    width: 120,
    sortable: false,
    renderCell: (params: any) => (
      <PlanOptimizationCell plan={params.row} />
    )
  },
  {
    field: 'google_ortools_calendar',
    headerName: t('plans.googleCalendar'),
    width: 120,
    sortable: false,
    renderCell: (params: any) => (
      <GoogleORToolsCalendarPlanningCell plan={params.row} />
    )
  },
  {
    field: 'actions',
    headerName: t('common.actions'),
    width: 250,
    sortable: false,
    renderCell: (params: any) => (
      <Stack direction="row" spacing={1}>
        <IconButton onClick={() => handleViewDetail(params.row)} size="small" title={t('plans.viewDetails')}>
          <SettingsIcon />
        </IconButton>
        <IconButton onClick={() => handleViewClusters(params.row)} size="small" title={t('plans.viewClusters')}>
          <GroupWorkIcon />
        </IconButton>
        <IconButton onClick={() => handleViewOptimizationResults(params.row)} size="small" title={t('plans.viewOptimizationResults')}>
          <AssessmentIcon />
        </IconButton>
        <IconButton onClick={() => handleEdit(params.row)} size="small" title={t('common.edit')}>
          <EditIcon />
        </IconButton>
        <IconButton onClick={() => handleExportExcel(params.row)} size="small" title={t('plans.exportExcel')}>
          <DownloadIcon />
        </IconButton>
        <IconButton onClick={() => handleExportJson(params.row)} size="small" title="Export JSON">
          <CodeIcon />
        </IconButton>
        <IconButton onClick={() => handleDeleteClick(params.row.id!)} size="small" title="Delete">
          <DeleteIcon />
        </IconButton>
      </Stack>
    ),
  },
], [handleViewDetail, handleEdit, handleDeleteClick, handleExportExcel, handleExportJson, handleViewClusters, handleViewOptimizationResults]);

// Month options generation moved to PlanFormDialog component

  const [mainTab, setMainTab] = useState(0);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('plans.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateForm(true)}
        >
          {t('plans.createNew')}
        </Button>
      </Stack>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label={t('plans.planManagement')} icon={<AssignmentIcon />} />
          <Tab label={t('plans.orToolsOptimization')} icon={<RouteIcon />} />
          <Tab label={t('plans.routeDetails')} icon={<DirectionsCarIcon />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {mainTab === 0 && (
        <>
          {/* Task Queue Status */}
          <TaskQueueStatus showCompact={true} />

          <DataTable
            rows={plans}
            columns={columns}
            loading={isLoading}
            onRowDoubleClick={handleEdit}
          />
        </>
      )}

      {mainTab === 1 && (
        <PlanOptimizationView />
      )}

      {mainTab === 2 && selectedPlan && (
        <OptimizationRouteView 
          planId={selectedPlan.id}
          showMap={true}
          showTimeline={true}
        />
      )}

      {/* Create Plan Dialog */}
      <PlanFormDialog
        mode="create"
        open={openCreateForm}
        onClose={() => setOpenCreateForm(false)}
        programs={programs}
        workers={workers}
        regions={regions}
        settlements={settlements}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
      />

      {/* Edit Plan Dialog */}
      <PlanFormDialog
        mode="edit"
        open={openEditForm}
        onClose={() => setOpenEditForm(false)}
        plan={selectedPlan}
        programs={programs}
        workers={workers}
        regions={regions}
        settlements={settlements}
        onSubmit={handleUpdateSubmit}
        isLoading={updateMutation.isPending}
      />

      {/* Plan Detail Dialog */}
      <Dialog open={openPlanDetail} onClose={() => setOpenPlanDetail(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {t('plans.planDetails')}: {selectedPlan?.name}
        </DialogTitle>
        <Box sx={{ pl: 3, pt: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" component="span">
            {selectedPlan?.start_date ? `Start: ${selectedPlan.start_date}` : ''}
            {selectedPlan?.days ? `, Days: ${selectedPlan.days}` : ''}
          </Typography>
        </Box>
        <DialogContent>
          {planDetailData && (
            <Stack spacing={3}>
              {/* Plan Statistics */}
              {selectedPlan?.stats && (
                <PlanStatsCard stats={selectedPlan.stats} />
              )}
              
              {/* Program Stores Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('plans.programStores')} ({planDetailData.plan_program_stores.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Program</TableCell>
                          <TableCell>Store</TableCell>
                          <TableCell>Visit Time (min)</TableCell>
                          <TableCell>Rate</TableCell>
                          <TableCell>Planning</TableCell>
                          <TableCell>Actions</TableCell>                          
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {planDetailData.plan_program_stores.map((pps) => (
                          <TableRow 
                            key={pps.id}
                            sx={{
                              backgroundColor: (
                                pps.visiting_hours_modified || 
                                pps.visit_time_minutes_modified || 
                                pps.available_time_windows_modified || 
                                pps.rate_modified
                              ) ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell>{pps.program_name}</TableCell>
                            <TableCell>
                              {pps.store_name}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {pps.store_address}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={pps.visit_time_minutes_override ?? pps.original_visit_time_minutes ?? ''}
                                onChange={(e) => handleProgramStoreUpdate(
                                  pps.id, 
                                  'visit_time_minutes_override', 
                                  parseInt(e.target.value) || null
                                )}
                                sx={{
                                  width: 80,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: pps.visit_time_minutes_modified ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ step: 0.01 }}
                                value={pps.rate_override ?? pps.original_rate ?? ''}
                                onChange={(e) => handleProgramStoreUpdate(
                                  pps.id, 
                                  'rate_override', 
                                  parseFloat(e.target.value) || null
                                )}
                                sx={{
                                  width: 100,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: pps.rate_modified ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {(pps.visiting_hours_modified || pps.visit_time_minutes_modified || 
                                pps.available_time_windows_modified || pps.rate_modified) && (
                                <Chip label="Modified" size="small" color="warning" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Workers Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('navigation.workers')} ({planDetailData.plan_workers.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Worker</TableCell>
                          <TableCell>Pricing</TableCell>
                          <TableCell>Work Time</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {planDetailData.plan_workers.map((pw) => (
                          <TableRow 
                            key={pw.id}
                            sx={{
                              backgroundColor: (
                                pw.availability_hours_modified || 
                                pw.pricing_modified || 
                                pw.work_time_modified || 
                                pw.default_timings_modified
                              ) ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell>
                              {pw.worker_name}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {pw.worker_email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ step: 0.01 }}
                                value={pw.pricing_override ?? pw.original_pricing ?? ''}
                                onChange={(e) => handleWorkerUpdate(
                                  pw.id, 
                                  'pricing_override', 
                                  parseFloat(e.target.value) || null
                                )}
                                sx={{
                                  width: 100,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: pw.pricing_modified ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={pw.work_time_override ?? pw.original_work_time ?? ''}
                                onChange={(e) => handleWorkerUpdate(
                                  pw.id, 
                                  'work_time_override', 
                                  parseInt(e.target.value) || null
                                )}
                                sx={{
                                  width: 80,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: pw.work_time_modified ? 'rgba(255, 193, 7, 0.1)' : 'inherit'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {(pw.availability_hours_modified || pw.pricing_modified || 
                                pw.work_time_modified || pw.default_timings_modified) && (
                                <Chip label="Modified" size="small" color="warning" />
                              )}
                            </TableCell>
                            <TableCell>
                              {(pw.availability_hours_modified || pw.pricing_modified || 
                                pw.work_time_modified || pw.default_timings_modified) && (
                                <Chip label="Modified" size="small" color="warning" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Time Matrix Section - Temporarily disabled */}
              {/* <Card>
                <CardContent>
                  <TimeMatrixEditor
                    initialMatrix={
                      (() => {
                        const n = (planDetailData.plan_workers?.length || 0) + (planDetailData.plan_program_stores?.length || 0);
                        return Array.from({ length: n }, (_, i) => Array(n).fill(0));
                      })()
                    }
                    rowLabels={
                      [
                        ...(planDetailData.plan_workers?.map(w => w.worker_name || `Worker ${w.id}`) || []),
                        ...(planDetailData.plan_program_stores?.map(s => s.store_name || `Store ${s.id}`) || [])
                      ]
                    }
                    colLabels={
                      [
                        ...(planDetailData.plan_workers?.map(w => w.worker_name || `Worker ${w.id}`) || []),
                        ...(planDetailData.plan_program_stores?.map(s => s.store_name || `Store ${s.id}`) || [])
                      ]
                    }
                    onMatrixChange={() => {}}
                    onSave={async (matrix: string[][], meta: any) => {
                      if (!planDetailData?.id) return;
                      const provider = meta?.provider || 'local';
                      const routeType = meta?.tabKey || meta?.routeType || 'auto';
                      function timeStringToMinutes(time: string): number {
                        const [h, m] = time.split(':').map(Number);
                        return h * 60 + m;
                      }
                      const numberMatrix = matrix.map((row: string[]) => row.map((cell: string) => timeStringToMinutes(cell)));
                      await savePlanTimeMatrix(planDetailData.id, provider, routeType, numberMatrix);
                      setSnackbarMsg('Матрица времени успешно сохранена');
                      setSnackbarOpen(true);
                    }}
                    onRequestMatrix={async (provider: any, routeType: any) => {
                      if (!planDetailData?.id) return {};
                      let data;
                      if (routeType === 'all') {
                        data = await getPlanTimeMatrix(planDetailData.id, provider, 'auto,public_transport,pedestrian');
                      } else {
                        data = await getPlanTimeMatrix(planDetailData.id, provider, routeType);
                      }
                      setUserTimeMatrices(data?.user_time_matrices || {});
                      setTimeMatrices(data?.time_matrices || {});
                      setMatrixLogs(data?.logs || null);
                      return data?.time_matrices || {};
                    }}
                  />
                </CardContent>
              </Card> */}
              {/* Planning Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Planning Tools
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" color="text.secondary">
                      Yandex Calendar Planning:
                    </Typography>
                    <PlanOptimizationCell plan={planDetailData} />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Google OR-Tools Calendar Planning:
                    </Typography>
                    <GoogleORToolsCalendarPlanningCell plan={planDetailData} />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Time Matrix:
                    </Typography>
                    {matrixMeta?.has_matrices ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={async () => {
                            // Load matrices and open viewer
                            await loadMatricesFromMetadata();
                            setOpenTimeMatrixViewer(true);
                          }}
                          startIcon={<EditIcon />}
                        >
                          View/Edit Matrix
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setOpenTimeMatrixRequest(true)}
                          startIcon={<RefreshIcon />}
                          title="Request fresh matrices from provider"
                        >
                          Refresh from Provider
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenTimeMatrixRequest(true)}
                        startIcon={<DownloadIcon />}
                      >
                        Request Matrix
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanDetail(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this plan? This action cannot be undone."
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Time Matrix Request Dialog */}
      <TimeMatrixRequestDialog
        open={openTimeMatrixRequest}
        onClose={() => setOpenTimeMatrixRequest(false)}
        planId={selectedPlan?.id || 0}
        onMatrixReceived={useCallback(async (data: any) => {
          setUserTimeMatrices(data?.user_time_matrices || {});
          setTimeMatrices(data?.time_matrices || {});
          setMatrixLogs(data?.logs || null);
          setCurrentProvider(data?.provider || 'doublegis');
          
          // Refresh matrix metadata to show updated state
          if (selectedPlan?.id) {
            await fetchPlanDetail(selectedPlan.id);
          }
          
          if (data?.time_matrices && Object.keys(data.time_matrices).length > 0) {
            setSnackbarMsg(`Time matrix generated successfully for ${Object.keys(data.time_matrices).length} route types`);
          } else {
            setSnackbarMsg('Time matrix requested but no data received');
          }
          setSnackbarOpen(true);
        }, [selectedPlan])}
      />

      {/* Time Matrix Viewer */}
      <TimeMatrixViewer
        open={openTimeMatrixViewer}
        onClose={() => setOpenTimeMatrixViewer(false)}
        planId={selectedPlan?.id || 0}
        timeMatrices={timeMatrices}
        userTimeMatrices={userTimeMatrices}
        points={useMemo(() => 
          planDetailData ? [
            ...(planDetailData.plan_workers?.map(pw => ({
              id: pw.worker_id,
              name: pw.worker_name || `Worker ${pw.worker_id}`,
              type: 'Worker'
            })) || []),
            ...(planDetailData.plan_program_stores?.map(pps => ({
              id: pps.program_store_id,
              name: pps.store_name || `Store ${pps.program_store_id}`,
              type: 'Store'
            })) || [])
          ] : [], [planDetailData]
        )}
        provider={currentProvider}
        onMatrixSaved={useCallback(async (savedMatrix: { routeType: string; matrix: number[][] }) => {
          setSnackbarMsg('Time matrix saved successfully');
          setSnackbarOpen(true);
          
          // Update the userTimeMatrices state with the saved matrix
          setUserTimeMatrices(prev => ({
            ...prev,
            [savedMatrix.routeType]: savedMatrix.matrix
          }));
        }, [])}
      />

      {/* Clusters View Dialog */}
      <Dialog
        open={openClustersView}
        onClose={() => setOpenClustersView(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">{t('plans.viewClusters')}</Typography>
            <IconButton onClick={() => setOpenClustersView(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {clustersPlanData && (
            <PlanClustersView
              planId={clustersPlanData.id}
              planName={clustersPlanData.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Optimization Results Dialog */}
      <Dialog
        open={openOptimizationResults}
        onClose={() => setOpenOptimizationResults(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">{t('plans.viewOptimizationResults')} - {optimizationPlanData?.name}</Typography>
            <IconButton onClick={() => setOpenOptimizationResults(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {optimizationPlanData && (
            <OptimizationResultsView planId={optimizationPlanData.id} />
          )}
        </DialogContent>
      </Dialog>

      {matrixLogs && (
        <Box mt={2}>
          {matrixLogs.stores_without_coords && matrixLogs.stores_without_coords.length > 0 && (
            <Box mb={1}>
              <Typography variant="subtitle2" color="error">{t('plans.storesWithoutCoordinates')}:</Typography>
              <ul style={{ margin: 0 }}>
                {matrixLogs.stores_without_coords.map((s: any) => (
                  <li key={s.id}>{s.id}: {s.address}</li>
                ))}
              </ul>
            </Box>
          )}
          {matrixLogs.workers_without_coords && matrixLogs.workers_without_coords.length > 0 && (
            <Box mb={1}>
              <Typography variant="subtitle2" color="error">{t('plans.merchandisersWithoutCoordinates')}:</Typography>
              <ul style={{ margin: 0 }}>
                {matrixLogs.workers_without_coords.map((w: any) => (
                  <li key={w.id}>{w.id}: {w.name} ({w.email})</li>
                ))}
              </ul>
            </Box>
          )}
          {/* Универсальное отображение других предупреждений/логов */}
          {Object.entries(matrixLogs)
            .filter(([key]) => key !== 'stores_without_coords' && key !== 'workers_without_coords')
            .map(([key, value]) => (
              <Box mb={1} key={key}>
                <Typography variant="subtitle2" color="warning.main">{key}:</Typography>
                <pre style={{ margin: 0, background: '#f9f9f9', padding: 8, borderRadius: 4 }}>{JSON.stringify(value, null, 2)}</pre>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default PlansPage; 